/* ============================================================
 * frontend-v3/src/screens/content/EditMetadata.tsx
 *
 * T316 / V3I-FR-005 — Category A1 decomposition: EditMetadata Save.
 *
 * Per PRD §7.1 A1 the Save button wires
 *   `useMutationWithToast(api.mutations.assets.updateAsset, {...})`
 * with `logLabel: "edit-metadata-save"` and `mutationName:
 * "assets.updateAsset"` (RETRY_SAFE entry added by T316 to
 * `frontend-v3/src/lib/mutation-policy.ts` — patch-style, last-write-wins).
 *
 * Args contract (verified at convex/mutations/assets.ts:140):
 *   assetId: Id<"assets">
 *   title?:  string
 *   description?: string
 *   tags?:   string[]
 *   assetType?: <branded literal> (optional — not exposed by this form yet)
 *
 * `argKeys` per V3I-INV-12: every call from this site emits exactly
 *   ["assetId", "title", "description", "tags"]
 * (the form does NOT pass `assetType`; the value is left unmanaged so the
 * mutation handler's optional branch falls through). No newly-wired
 * mutation may log argument VALUES; only the key set.
 *
 * Decomposition notes (PRD §12 deliberate rejection 7):
 *   - Per-surface decomposition at wiring time. The bundle file
 *     keeps every unrelated component; only this surface's body moves.
 *   - The pending/_bundle.tsx re-exports `EditMetadata` from THIS file
 *     post-decomposition so any incidental importer keeps working.
 *
 * Honest-state contract (CLAUDE.md §5):
 *   - aDoc === undefined → Skeleton (loading)
 *   - aDoc === null      → EmptyState (not found, honest)
 *   - aDoc resolved      → form
 * ============================================================ */
import { useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import type { Id } from '@convex/dataModel';
import {
  Btn, EmptyState, Field, Input, PageHead, Panel,
  SegmentedControl, Skeleton, Textarea,
} from '../../components/primitives';
import { TagEditor, type TagEntry } from '../../components/tags/TagEditor';
import { navigate } from '../../lib/nav';
import { useMutationWithToast } from '../../hooks/useMutationWithToast';
import { isConvexId } from '../../lib/ids';

type AssetDoc = {
  _id?: string;
  title?: string;
  description?: string;
  tags?: string[];
} | null | undefined;

export function EditMetadata({ id }: { id?: string }) {
  const aDoc = useQuery(
    api.assetQueries.getAssetById,
    isConvexId(id) ? { assetId: id as unknown as Id<'assets'> } : 'skip',
  ) as AssetDoc;

  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  // Typed tag list. `tagsTouched` distinguishes "operator edited tags" from
  // "still showing the doc's tags", so an un-touched save mirrors the doc.
  const [tagEntries, setTagEntries] = useState<TagEntry[]>([]);
  const [tagsTouched, setTagsTouched] = useState<boolean>(false);

  const resolvedTitle = aDoc?.title ?? '';
  const resolvedDescription = aDoc?.description ?? '';
  const resolvedTags = aDoc?.tags ?? [];
  const effectiveTitle = title || resolvedTitle;
  const effectiveDescription = description || resolvedDescription;
  // Un-touched: show the doc's free-form tags. Touched: the session list.
  const effectiveTags: TagEntry[] = tagsTouched
    ? tagEntries
    : resolvedTags.map((t) => ({ tag: t, kind: 'freeform' as const }));

  const { run: updateAsset, loading } = useMutationWithToast(
    api.mutations.assets.updateAsset,
    {
      logLabel: 'edit-metadata-save',
      mutationName: 'assets.updateAsset',
    },
  );

  const { run: applyAssetTags, loading: tagsLoading } = useMutationWithToast(
    api.mutations.assets.applyAssetTags,
    {
      logLabel: 'edit-metadata-tags',
      mutationName: 'assets.applyAssetTags',
      successMessage: 'Metadatos guardados',
    },
  );

  if (id && aDoc === undefined) {
    return <Skeleton h={420} />;
  }
  if (id && aDoc === null) {
    return (
      <EmptyState
        icon="alertTriangle"
        title="Video no encontrado"
        body="No se localizó el video solicitado."
      />
    );
  }

  const assetId = (aDoc?._id ?? '') as string;
  const displayTitle = effectiveTitle || '—';

  async function onSave() {
    if (!assetId) return;
    try {
      // Title/description via updateAsset (no tags — tags are owned by the
      // typed applyAssetTags entry point below, which mirrors assets.tags).
      await updateAsset({
        assetId: assetId as unknown as Id<'assets'>,
        title: effectiveTitle,
        description: effectiveDescription,
      });
      // NMW-397: persist the faceted tag set. Replace-to-match; the mutation
      // mirrors the flattened set back into assets.tags.
      await applyAssetTags({
        assetId: assetId as unknown as Id<'assets'>,
        tags: effectiveTags.map((e) => ({ tag: e.tag, kind: e.kind })),
      });
      navigate('/library/' + assetId);
    } catch {
      // The wrapper has already surfaced the toast; suppress the
      // unhandled rejection so the navigation does not fire on failure.
    }
  }

  return (
    <div style={{ maxWidth: 820, margin: '0 auto' }}>
      <PageHead
        crumbs={[
          { label: 'Contenido', href: '#/library' },
          { label: displayTitle, href: '#/library/' + assetId },
          { label: 'Metadatos' },
        ]}
        title="Editar metadatos"
      />
      <Panel pad={28}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          <div
            className="t-mono"
            style={{ fontSize: 11, color: 'var(--muted-slate)' }}
          >
            Identidad
          </div>
          <Field label="Título">
            <Input value={effectiveTitle} onChange={setTitle} />
          </Field>
          <Field label="Descripción">
            <Textarea
              value={effectiveDescription}
              onChange={setDescription}
              placeholder="Describe este contenido…"
            />
          </Field>
          <div style={{ height: 1, background: 'var(--lightest-gray)' }} />
          <div
            className="t-mono"
            style={{ fontSize: 11, color: 'var(--muted-slate)' }}
          >
            Clasificación
          </div>
          <Field label="Tipo de contenido">
            <SegmentedControl
              value="VOD"
              onChange={() => {}}
              options={[
                { value: 'VOD', label: 'VOD' },
                { value: 'Filler', label: 'Relleno' },
                { value: 'House Ad', label: 'Anuncio' },
                { value: 'Bumper', label: 'Bumper' },
              ]}
            />
          </Field>
          <div className="circa-grid-2-tight">
            <Field label="Categoría">
              <Input value="Noticias" onChange={() => {}} />
            </Field>
            <Field label="Clasificación por edad">
              <Input value="TP" onChange={() => {}} />
            </Field>
          </div>
          <Field label="Etiquetas">
            {/* NMW-410: shared <TagEditor> (chips + autocomplete over the
                canonical tagRegistry). Controlled — the parent owns the list
                and persists it via applyAssetTags on Save. */}
            <TagEditor
              value={effectiveTags}
              onChange={(next) => {
                setTagEntries(next);
                setTagsTouched(true);
              }}
            />
          </Field>
          <div style={{ height: 1, background: 'var(--lightest-gray)' }} />
          <div
            className="t-mono"
            style={{ fontSize: 11, color: 'var(--muted-slate)' }}
          >
            Metadatos de programa (guía / EPG)
          </div>
          <div className="circa-grid-2-tight">
            <Field label="Serie">
              <Input value="" onChange={() => {}} />
            </Field>
            <Field label="Categoría IAB">
              <Input value="IAB12" onChange={() => {}} />
            </Field>
            <Field label="Temporada">
              <Input value="" onChange={() => {}} />
            </Field>
            <Field label="Episodio">
              <Input value="" onChange={() => {}} />
            </Field>
          </div>
          <div>
            <Btn variant="solid" loading={loading || tagsLoading} onClick={() => void onSave()}>
              Guardar metadatos
            </Btn>
          </div>
        </div>
      </Panel>
    </div>
  );
}

export default EditMetadata;
